/**
 * @package   EGI / Frontend — cart
 * @author    Padmin D. Curtis (AI Partner OS3.0) for Fabio Cherici
 * @version   1.0.0 (M-EGI-385 passo 3)
 * @date      2026-07-05
 * @purpose   Driver server del carrello — UNICO driver (il cart non ha modalità
 *            guest, ADR-C7). Shape verificate alla fonte (CartController):
 *            add/remove → {success,in_cart,cart_count,wishlist_count,message},
 *            index → {items,cart_count,cart_total}, count → {cart_count}.
 *            Pattern WishlistServerDriver: CSRF + credentials same-origin +
 *            AbortController per-risorsa; errori sistema → ponte UEM condiviso
 *            (uem-report, codici CART_MOVE_FAILED/CART_LIST_FAILED registrati);
 *            il refusal 422 di dominio (reason granulare) NON passa da UEM
 *            (precedente P3-1) → CartRefusalError col message i18n del server.
 */

import { getCsrfTokenTS } from '../utils/csrf';
import { isAbortError } from '../wishlist/server-driver';
import { reportClientError, reportServerError } from '../wishlist/uem-report';
import { CartRefusalError, type CartEndpoints } from './types';

function asRecord(value: unknown): Record<string, unknown> {
    if (typeof value !== 'object' || value === null) {
        throw new Error('Cart API: unexpected response shape');
    }
    return value as Record<string, unknown>; // narrow post-guard: lettura chiavi tipizzata sotto
}

function toCount(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 0;
}

export interface CartMutationResult {
    inCart: boolean;
    cartCount: number;
    wishlistCount: number;
    /** Messaggio i18n risolto dal server (cart.added / cart.removed / …). */
    message: string | null;
}

export class CartServerDriver {
    private readonly aborts = new Map<string, AbortController>();

    public constructor(private readonly endpoints: CartEndpoints) {}

    /** T1/T1b — move-to-cart. Refusal 422 → CartRefusalError (mai UEM). */
    public async add(id: string): Promise<CartMutationResult> {
        const data = await this.request(
            this.urlFor(this.endpoints.add, id),
            { method: 'POST', signal: this.takeAbortSignal('egi-' + id) },
            'CART_MOVE_FAILED',
            { egi_id: id, action: 'add' },
        );
        return this.toMutationResult(data);
    }

    /** T2 — la riga TORNA a wishlist (ADR-C2). Idempotente. */
    public async remove(id: string): Promise<CartMutationResult> {
        const data = await this.request(
            this.urlFor(this.endpoints.remove, id),
            { method: 'DELETE', signal: this.takeAbortSignal('egi-' + id) },
            'CART_MOVE_FAILED',
            { egi_id: id, action: 'remove' },
        );
        return this.toMutationResult(data);
    }

    /** Badge navbar — count leggero. */
    public async count(): Promise<number> {
        const data = await this.request(
            this.endpoints.count,
            { method: 'GET' },
            'CART_LIST_FAILED',
            { action: 'count' },
        );
        return toCount(asRecord(data).cart_count);
    }

    private toMutationResult(data: unknown): CartMutationResult {
        const record = asRecord(data);
        return {
            inCart: record.in_cart === true,
            cartCount: toCount(record.cart_count),
            wishlistCount: toCount(record.wishlist_count),
            message: typeof record.message === 'string' ? record.message : null,
        };
    }

    private urlFor(template: string, id: string): string {
        return template.replace('__ID__', encodeURIComponent(id));
    }

    /** Aborta l'eventuale richiesta in volo sulla stessa risorsa (pattern likeService). */
    private takeAbortSignal(key: string): AbortSignal {
        this.aborts.get(key)?.abort();
        const controller = new AbortController();
        this.aborts.set(key, controller);
        return controller.signal;
    }

    private async request(
        url: string,
        init: RequestInit,
        errorCode: string,
        context: Record<string, unknown>,
    ): Promise<unknown> {
        let response: Response;
        try {
            response = await fetch(url, {
                ...init,
                credentials: 'same-origin', // ESSENZIALE: sessione FegiAuth via cookie
                headers: {
                    'X-CSRF-TOKEN': getCsrfTokenTS(),
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });
        } catch (error) {
            if (isAbortError(error)) {
                throw error; // richiesta superata da una più recente: non è un errore
            }
            reportClientError(
                errorCode,
                { ...context, error: error instanceof Error ? error.message : String(error) },
                error instanceof Error ? error : undefined,
            );
            throw error;
        }

        if (!response.ok) {
            const errorData: unknown = await response.json().catch(() => null);

            // 422 di dominio con reason = refusal (§2.3), FUORI dal ponte UEM
            if (response.status === 422 && errorData !== null) {
                const body = errorData as Record<string, unknown>;
                if (typeof body.reason === 'string') {
                    throw new CartRefusalError(
                        body.reason,
                        typeof body.message === 'string' ? body.message : '',
                    );
                }
            }

            reportServerError(errorCode, errorData, { ...context, status: response.status });
            throw new Error('Cart API: HTTP ' + String(response.status));
        }

        return (await response.json()) as unknown;
    }
}
